/**
 * The agent's copy of the screen (RFC 0304 3d, S3).
 *
 * The person sends from a space, and the space shows more than the thread:
 * a document beside it, a review, a terminal. Without this a message saying
 * "change this" reaches an agent that cannot see which "this". The window
 * stamps the panes onto the message at send (`viewJson`); this renders the
 * latest stamp as a block beside `<holdings>`, served for the turn and never
 * drawn in the transcript.
 *
 * IDS IN FULL, for the reason `formatHoldings` gives: an abbreviated id is one
 * the agent cannot pass to `read_document`.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { decodeLogDoc } from '../log/codec'
import { projectView } from '../log/projection-view'
import type { MessageView, ViewPane } from '../log/types'

function paneLine(pane: ViewPane, sessionId: string): string {
  const parts: string[] = [pane.kind.replace('-', ' ')]
  if (pane.title) parts.push(`"${pane.title}"`)
  if (pane.id) parts.push(`(${pane.id})`)
  if (pane.kind === 'session' && pane.id === sessionId)
    parts.push('this conversation')
  if (pane.focused) parts.push('FOCUSED')
  return `  ${parts.join('  ')}`
}

/**
 * The `<view>` block, or '' when the screen adds nothing.
 *
 * NOTHING TO ADD is a view that holds only this conversation's own thread:
 * the agent already knows it is being talked to. '' rather than an empty
 * block, matching `formatHoldings`, so a host can append unconditionally.
 */
export function formatView(
  view: MessageView | null | undefined,
  sessionId: string,
): string {
  if (!view?.panes.length) return ''
  const beyondThread = view.panes.some(
    (p) => !(p.kind === 'session' && p.id === sessionId),
  )
  if (!beyondThread) return ''
  return [
    '<view>',
    'What was on screen when the person sent their last message, pane by ' +
      'pane' +
      (view.space ? `, in the space "${view.space}"` : '') +
      '. When they say "this" or "here" without naming it, they mean the ' +
      'FOCUSED pane, or the pane beside this conversation if the ' +
      'conversation itself had focus. Read a document before changing it.',
    ...view.panes.map((p) => paneLine(p, sessionId)),
    '</view>',
  ].join('\n')
}

/** As `readHoldingsPrompt`: bounded, and '' rather than a throw. */
const VIEW_TIMEOUT_MS = 5000

export async function readViewPrompt(
  repo: Repo,
  sessionId: string,
  opts: { timeoutMs?: number } = {},
): Promise<string> {
  if (!sessionId) return ''
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const doc = await Promise.race([
      (async () => {
        const handle = await repo.find(sessionId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        return handle.doc()
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(
          () => resolve(null),
          opts.timeoutMs ?? VIEW_TIMEOUT_MS,
        )
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
    if (!doc || typeof doc !== 'object') return ''
    return formatView(
      projectView(decodeLogDoc(doc as Record<string, unknown>)),
      sessionId,
    )
  } catch {
    return ''
  } finally {
    if (timer) clearTimeout(timer)
  }
}
